import { keepPreviousData, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { appelApi } from './api';
import { envoyerParMorceaux } from './depot-par-morceaux';
import type { NatureRessource } from './useLecteur';

// ---------------------------------------------------------------------------
// Création de cours (TF-442) : le cours d'un formateur, ses modules et ses
// leçons. Chaque écriture répond par le cours entier, qui remplace ce que
// l'écran affiche ; l'ordre du programme est enregistré après chaque
// glisser-déposer. « Mes cours » (TF-443) : la liste et le statut.
// ---------------------------------------------------------------------------

export interface FormationProposee {
  id: string;
  titre: string;
  rncp: string;
}

/** Bloc du programme d'une formation, que le cours couvre (TF-451). */
export interface BlocProgramme {
  id: string;
  libelle: string;
}

export interface LeconCours {
  id: string;
  titre: string;
  /** Vidéo lue par les apprenants (TF-444), ou `null`. */
  video: { nomFichier: string; tailleOctets: number; dureeSecondes: number | null; disponibleLe: string } | null;
  /** Dépôt en cours, à reprendre ; l'ancienne vidéo reste lisible pendant ce temps. */
  depot: { nomFichier: string; attendus: number; recus: number } | null;
  /** Texte horodaté : sous-titres et transcription à la fois (TF-445). */
  sousTitres: {
    source: SourceSousTitres;
    /** Fichier déposé ; `null` pour une transcription automatique. */
    nomFichier: string | null;
    deposesLe: string;
    corrigesLe: string | null;
  } | null;
  /** Transcription automatique de la vidéo (TF-445). */
  transcription: {
    statut: StatutTranscription | null;
    statutLibelle: string | null;
    message: string | null;
    demandeeLe: string | null;
    debuteeLe: string | null;
    refusLancement: string | null;
    refusCorrection: string | null;
  };
  /** Ressources annexes (TF-447), dans l'ordre d'ajout. */
  ressources: RessourceCours[];
  /** Ce qui empêche d'ajouter une ressource (cours archivé, plafond atteint), ou `null`. */
  refusAjoutRessource: string | null;
}

export interface RessourceCours {
  id: string;
  nature: NatureRessource;
  titre: string;
  /** Fichier déposé (PDF, ZIP) ; `null` pour un lien. */
  nomFichier: string | null;
  tailleOctets: number | null;
  /** Lien ; `null` pour un fichier. */
  adresse: string | null;
  domaine: string | null;
  ajouteeLe: string;
}

export type SourceSousTitres = 'FICHIER' | 'TRANSCRIPTION';

export type StatutTranscription = 'EN_ATTENTE' | 'EN_COURS' | 'TERMINEE' | 'ECHEC' | 'REFUSEE';

export function transcriptionEnCours(statut: StatutTranscription | null): boolean {
  return statut === 'EN_ATTENTE' || statut === 'EN_COURS';
}

export interface ModuleCours {
  id: string;
  titre: string;
  lecons: LeconCours[];
}

export type StatutCours = 'BROUILLON' | 'PUBLIE' | 'ARCHIVE';

export type GesteStatutCours = 'PUBLIER' | 'REPASSER_EN_BROUILLON' | 'ARCHIVER' | 'DESARCHIVER';

/** Geste proposé depuis le statut du cours ; `refus` nomme ce qui le bloque. */
export interface GestePossible {
  geste: GesteStatutCours;
  libelle: string;
  statut: StatutCours;
  refus: string | null;
}

export interface Cours {
  id: string;
  titre: string;
  description: string | null;
  statut: StatutCours;
  statutLibelle: string;
  formation: FormationProposee & { actif: boolean };
  /** Bloc du programme couvert ; `null` : cours transversal. */
  bloc: BlocProgramme | null;
  auteur: string;
  modifieLe: string;
  /** Ce qui empêche de modifier le cours (archivé), ou `null`. */
  refusModification: string | null;
  gestes: GestePossible[];
  /** Whisper est raccordé : chaque nouvelle vidéo est transcrite. */
  transcriptionRaccordee: boolean;
  /** Ce que l'écran annonce et contrôle avant l'envoi d'une ressource ; l'API juge. */
  limitesRessources: { extensions: string[]; libelleFormats: string; tailleMaxOctets: number; libelleTailleMax: string };
  modules: ModuleCours[];
}

export interface LigneCours {
  id: string;
  titre: string;
  statut: StatutCours;
  statutLibelle: string;
  formation: FormationProposee & { actif: boolean };
  bloc: BlocProgramme | null;
  auteur: string;
  nombreModules: number;
  nombreLecons: number;
  modifieLe: string;
  gestes: GestePossible[];
}

export interface ListeCours {
  /** `TOUS` : la direction voit les cours de tous les auteurs. */
  perimetre: 'AUTEUR' | 'TOUS';
  totaux: Record<StatutCours, number>;
  compteurs: Record<StatutCours, number>;
  formations: (FormationProposee & { actif: boolean })[];
  cours: LigneCours[];
}

export interface FiltresCours {
  recherche: string;
  formationId: string;
  statut: StatutCours | '';
}

export interface InformationsCours {
  titre: string;
  formationId: string;
  /** Vide : cours transversal. */
  blocId: string;
  description: string;
}

/** Ordre affiché : les modules dans l'ordre, chacun avec ses leçons. */
export interface OrdreProgramme {
  modules: { id: string; lecons: string[] }[];
}

function cleCours(id: string): string[] {
  return ['cours', id];
}

const CLE_LISTE = ['cours', 'liste'];

function chemin(coursId: string, suite = ''): string {
  return `/cours/${encodeURIComponent(coursId)}${suite}`;
}

function apresEcriture(client: QueryClient, cours: Cours): void {
  client.setQueryData(cleCours(cours.id), cours);
  void client.invalidateQueries({ queryKey: CLE_LISTE });
}

export function useMesCours(filtres: FiltresCours) {
  return useQuery({
    queryKey: [...CLE_LISTE, filtres],
    queryFn: () => {
      const parametres = new URLSearchParams();
      if (filtres.recherche) parametres.set('recherche', filtres.recherche);
      if (filtres.formationId) parametres.set('formationId', filtres.formationId);
      if (filtres.statut) parametres.set('statut', filtres.statut);
      const requete = parametres.toString();
      return appelApi<ListeCours>(requete ? `/cours?${requete}` : '/cours');
    },
    placeholderData: keepPreviousData,
  });
}

/** Publier, repasser en brouillon, archiver, désarchiver. */
export function useChangerStatutCours() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ coursId, geste }: { coursId: string; geste: GesteStatutCours }) =>
      appelApi<Cours>(chemin(coursId, '/statut'), { methode: 'POST', corps: { geste } }),
    onSuccess: (cours) => apresEcriture(client, cours),
    // Le statut a pu changer ailleurs : on relit ce que l'écran affiche.
    onError: (_erreur, { coursId }) => {
      void client.invalidateQueries({ queryKey: CLE_LISTE });
      void client.invalidateQueries({ queryKey: cleCours(coursId) });
    },
  });
}

export function useOptionsCours() {
  return useQuery({
    queryKey: ['cours', 'options'],
    queryFn: () => appelApi<{ formations: (FormationProposee & { blocs: BlocProgramme[] })[] }>('/cours/options'),
    staleTime: 60_000,
  });
}

/** Une transcription attendue ou en cours se suit à l'écran sans recharger la page. */
const SUIVI_TRANSCRIPTION_MS = 15_000;

export function useCours(id: string | null) {
  return useQuery({
    queryKey: cleCours(id ?? ''),
    queryFn: () => appelApi<Cours>(chemin(id ?? '')),
    enabled: Boolean(id),
    refetchInterval: (requete) =>
      requete.state.data?.modules.some((module) =>
        module.lecons.some((lecon) => transcriptionEnCours(lecon.transcription.statut)),
      )
        ? SUIVI_TRANSCRIPTION_MS
        : false,
  });
}

export function useEnregistrerInformations(coursId: string | null) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (informations: InformationsCours) => {
      const corps = {
        titre: informations.titre,
        formationId: informations.formationId,
        blocId: informations.blocId || null,
        description: informations.description.trim() || null,
      };
      return coursId
        ? appelApi<Cours>(chemin(coursId), { methode: 'PATCH', corps })
        : appelApi<Cours>('/cours', { methode: 'POST', corps });
    },
    onSuccess: (cours) => apresEcriture(client, cours),
  });
}

export function useEnregistrerOrdre(coursId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (ordre: OrdreProgramme) => appelApi<Cours>(chemin(coursId, '/ordre'), { methode: 'PUT', corps: ordre }),
    onSuccess: (cours) => apresEcriture(client, cours),
    // L'affichage a pu diverger de la base : on relit le cours.
    onError: () => void client.invalidateQueries({ queryKey: cleCours(coursId) }),
  });
}

/** Ajout, renommage et retrait des modules et des leçons. */
export type GesteProgramme =
  | { geste: 'ajouter-module'; titre: string }
  | { geste: 'renommer-module'; moduleId: string; titre: string }
  | { geste: 'retirer-module'; moduleId: string }
  | { geste: 'ajouter-lecon'; moduleId: string; titre: string }
  | { geste: 'renommer-lecon'; leconId: string; titre: string }
  | { geste: 'retirer-lecon'; leconId: string };

function appelDuGeste(coursId: string, geste: GesteProgramme): Promise<Cours> {
  switch (geste.geste) {
    case 'ajouter-module':
      return appelApi<Cours>(chemin(coursId, '/modules'), { methode: 'POST', corps: { titre: geste.titre } });
    case 'renommer-module':
      return appelApi<Cours>(chemin(coursId, `/modules/${encodeURIComponent(geste.moduleId)}`), {
        methode: 'PATCH',
        corps: { titre: geste.titre },
      });
    case 'retirer-module':
      return appelApi<Cours>(chemin(coursId, `/modules/${encodeURIComponent(geste.moduleId)}`), {
        methode: 'DELETE',
      });
    case 'ajouter-lecon':
      return appelApi<Cours>(chemin(coursId, `/modules/${encodeURIComponent(geste.moduleId)}/lecons`), {
        methode: 'POST',
        corps: { titre: geste.titre },
      });
    case 'renommer-lecon':
      return appelApi<Cours>(chemin(coursId, `/lecons/${encodeURIComponent(geste.leconId)}`), {
        methode: 'PATCH',
        corps: { titre: geste.titre },
      });
    case 'retirer-lecon':
      return appelApi<Cours>(chemin(coursId, `/lecons/${encodeURIComponent(geste.leconId)}`), {
        methode: 'DELETE',
      });
  }
}

export function useGesteProgramme(coursId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (geste: GesteProgramme) => appelDuGeste(coursId, geste),
    onSuccess: (cours) => apresEcriture(client, cours),
  });
}

// ---------------------------------------------------------------------------
// Contenus d'une leçon (TF-444) : sa vidéo, déposée par morceaux, et ses
// sous-titres. Chaque geste répond par le cours entier.
// ---------------------------------------------------------------------------

function cheminLecon(coursId: string, leconId: string, suite: string): string {
  return chemin(coursId, `/lecons/${encodeURIComponent(leconId)}${suite}`);
}

/** Durée lue par le navigateur avant l'envoi ; `null` s'il ne la lit pas. */
export function lireDureeVideo(fichier: File): Promise<number | null> {
  return new Promise((resoudre) => {
    const adresse = URL.createObjectURL(fichier);
    const element = document.createElement('video');
    const finir = (duree: number | null) => {
      window.clearTimeout(delai);
      URL.revokeObjectURL(adresse);
      element.removeAttribute('src');
      resoudre(duree);
    };
    const delai = window.setTimeout(() => finir(null), 5000);
    element.preload = 'metadata';
    element.onloadedmetadata = () => finir(Number.isFinite(element.duration) ? element.duration : null);
    element.onerror = () => finir(null);
    element.src = adresse;
  });
}

export async function annoncerVideoLecon(coursId: string, leconId: string, fichier: File): Promise<Cours> {
  return appelApi<Cours>(cheminLecon(coursId, leconId, '/video/depot'), {
    methode: 'POST',
    corps: {
      nomFichier: fichier.name,
      typeMime: fichier.type,
      tailleOctets: fichier.size,
      dureeSecondes: await lireDureeVideo(fichier),
    },
  });
}

export function envoyerVideoLecon(
  coursId: string,
  leconId: string,
  fichier: File,
  onProgression: (recus: number) => void,
  signal?: AbortSignal,
): Promise<Cours> {
  const base = cheminLecon(coursId, leconId, '/video/depot');
  return envoyerParMorceaux<Cours>(
    { progression: base, morceau: (debut) => `${base}/morceaux?debut=${debut}`, finalisation: `${base}/finalisation` },
    fichier,
    onProgression,
    signal,
  );
}

/**
 * Texte d'un fichier de sous-titres : UTF-8, ou Windows-1252 pour un SubRip
 * enregistré par un ancien logiciel — sans quoi les accents deviendraient
 * des « � ».
 */
export async function lireSousTitres(fichier: File): Promise<string> {
  const octets = await fichier.arrayBuffer();
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(octets);
  } catch {
    return new TextDecoder('windows-1252').decode(octets);
  }
}

export type GesteContenu =
  | { geste: 'annuler-depot'; leconId: string }
  | { geste: 'retirer-video'; leconId: string }
  | { geste: 'deposer-sous-titres'; leconId: string; nomFichier: string; contenu: string }
  | { geste: 'retirer-sous-titres'; leconId: string }
  | { geste: 'lancer-transcription'; leconId: string }
  | { geste: 'ajouter-fichier-ressource'; leconId: string; fichier: File; titre: string }
  | { geste: 'ajouter-lien-ressource'; leconId: string; titre: string; adresse: string }
  | { geste: 'modifier-ressource'; leconId: string; ressourceId: string; titre: string; adresse?: string }
  | { geste: 'retirer-ressource'; leconId: string; ressourceId: string };

function appelDuGesteContenu(coursId: string, geste: GesteContenu): Promise<Cours> {
  switch (geste.geste) {
    case 'annuler-depot':
      return appelApi<Cours>(cheminLecon(coursId, geste.leconId, '/video/depot'), { methode: 'DELETE' });
    case 'retirer-video':
      return appelApi<Cours>(cheminLecon(coursId, geste.leconId, '/video'), { methode: 'DELETE' });
    case 'deposer-sous-titres':
      return appelApi<Cours>(cheminLecon(coursId, geste.leconId, '/sous-titres'), {
        methode: 'PUT',
        corps: { nomFichier: geste.nomFichier, contenu: geste.contenu },
      });
    case 'retirer-sous-titres':
      return appelApi<Cours>(cheminLecon(coursId, geste.leconId, '/sous-titres'), { methode: 'DELETE' });
    case 'lancer-transcription':
      return appelApi<Cours>(cheminLecon(coursId, geste.leconId, '/transcription'), { methode: 'POST' });
    case 'ajouter-fichier-ressource': {
      const formulaire = new FormData();
      formulaire.append('titre', geste.titre);
      formulaire.append('fichier', geste.fichier, geste.fichier.name);
      return appelApi<Cours>(cheminLecon(coursId, geste.leconId, '/ressources/fichier'), { methode: 'POST', formulaire });
    }
    case 'ajouter-lien-ressource':
      return appelApi<Cours>(cheminLecon(coursId, geste.leconId, '/ressources/lien'), {
        methode: 'POST',
        corps: { titre: geste.titre, adresse: geste.adresse },
      });
    case 'modifier-ressource':
      return appelApi<Cours>(cheminLecon(coursId, geste.leconId, `/ressources/${encodeURIComponent(geste.ressourceId)}`), {
        methode: 'PATCH',
        corps: { titre: geste.titre, adresse: geste.adresse },
      });
    case 'retirer-ressource':
      return appelApi<Cours>(cheminLecon(coursId, geste.leconId, `/ressources/${encodeURIComponent(geste.ressourceId)}`), {
        methode: 'DELETE',
      });
  }
}

export function useGesteContenu(coursId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (geste: GesteContenu) => appelDuGesteContenu(coursId, geste),
    onSuccess: (cours) => apresEcriture(client, cours),
  });
}

/** Après un dépôt (fini ou interrompu), le cours est relu ou remplacé. */
export function useApresDepot(coursId: string) {
  const client = useQueryClient();
  return {
    remplacer: (cours: Cours) => apresEcriture(client, cours),
    relire: () => client.invalidateQueries({ queryKey: cleCours(coursId) }),
  };
}

/**
 * Correction de la transcription (TF-445), depuis l'aperçu du lecteur : le
 * texte de chaque réplique, dans l'ordre lu, et la version lue. Le cours et la
 * leçon relus remplacent ce que les écrans affichent.
 */
export function useCorrigerTranscription(coursId: string, leconId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (correction: { version: string; textes: string[] }) =>
      appelApi<Cours>(cheminLecon(coursId, leconId, '/transcription'), { methode: 'PUT', corps: correction }),
    onSuccess: (cours) => {
      apresEcriture(client, cours);
      void client.invalidateQueries({ queryKey: ['lecteur', 'lecon', leconId] });
    },
  });
}

// ---------------------------------------------------------------------------
// Évaluations & quiz (TF-454)
// ---------------------------------------------------------------------------

export type TypeDevoirCours = 'QUIZ' | 'PROJET' | 'RAPPORT' | 'AUTRE';

export interface ReponseQuizCours {
  lettre: string;
  texte: string;
  correcte: boolean;
}

export interface QuestionQuizCours {
  id: string;
  enonce: string;
  explication: string | null;
  reponses: ReponseQuizCours[];
}

export interface DevoirDuCours {
  id: string;
  type: TypeDevoirCours;
  typeLibelle: string;
  titre: string;
  consigne: string | null;
  /** `null` : en fin de cours. */
  module: { id: string; titre: string } | null;
  bloc: { id: string; libelle: string };
  echeance: string;
  echeanceSaisie: { date: string; heure: string };
  dureeMinutes: number | null;
  publieLe: string | null;
  /** L'apprenant le voit : publié, et le cours aussi. */
  visible: boolean;
  questions: QuestionQuizCours[];
  /** Apprenants qui l'ont commencé ou rendu. */
  commences: number;
  /** Quiz faits, ou devoirs rendus. */
  rendus: number;
  refus: { publication: string | null; retrait: string | null; suppression: string | null; questions: string | null };
}

export interface LimitesDevoirsCours {
  titreMax: number;
  consigneMax: number;
  enonceMax: number;
  explicationMax: number;
  reponseMax: number;
  questionsMax: number;
  reponsesMin: number;
  reponsesMax: number;
  dureeMin: number;
  dureeMax: number;
  dureeParDefaut: number;
  heureParDefaut: string;
}

export interface DevoirsDuCours {
  coursId: string;
  statutCours: StatutCours;
  refusModification: string | null;
  /** L'auteur du cours s'il est formateur ; sinon le référent de chaque apprenant reçoit les dépôts. */
  formateur: string | null;
  modules: { id: string; titre: string }[];
  blocs: { id: string; libelle: string }[];
  blocDuCours: string | null;
  devoirs: DevoirDuCours[];
  limites: LimitesDevoirsCours;
}

export interface SaisieDevoir {
  type: TypeDevoirCours;
  titre: string;
  consigne: string | null;
  moduleId: string | null;
  blocId: string;
  echeance: { date: string; heure: string };
  dureeMinutes: number | null;
}

export interface SaisieQuestion {
  enonce: string;
  explication: string | null;
  reponses: { texte: string; correcte: boolean }[];
}

export type EtatResultat = 'A_FAIRE' | 'EN_COURS' | 'FAIT' | 'RENDU' | 'CORRIGE';

export interface ResultatsDevoir {
  devoir: { id: string; type: TypeDevoirCours; typeLibelle: string; titre: string; questions: number; echeance: string };
  visible: boolean;
  synthese: { concernes: number; faits: number; enCours: number; enRetard: number; moyenne: number | null };
  lignes: {
    apprenantId: string;
    nom: string;
    prenom: string;
    site: string;
    etat: EtatResultat;
    enRetard: boolean;
    bonnes: number | null;
    /** Note publiée par le correcteur (TF-457), à défaut la note automatique d'un quiz fait. */
    note: number | null;
    notePubliee: boolean;
    le: string | null;
    depots: number;
  }[];
}

function cleDevoirs(coursId: string) {
  return ['cours', coursId, 'devoirs'] as const;
}

export function useDevoirsCours(coursId: string) {
  return useQuery({
    queryKey: cleDevoirs(coursId),
    queryFn: () => appelApi<DevoirsDuCours>(chemin(coursId, '/devoirs')),
  });
}

/** Une ligne de « Quiz et exercices » (écran `quiz` du formateur ; la direction lit tous les cours). */
export interface LigneDevoirDuFormateur {
  id: string;
  type: TypeDevoirCours;
  typeLibelle: string;
  titre: string;
  cours: { id: string; titre: string; statut: StatutCours };
  module: string | null;
  bloc: string;
  echeance: string;
  echeanceLisible: string;
  publie: boolean;
  visible: boolean;
  questions: number;
  rendus: number;
  aCorriger: number;
  auteur: string | null;
}

export interface DevoirsDuFormateur {
  perimetre: 'AUTEUR' | 'TOUS';
  devoirs: LigneDevoirDuFormateur[];
  compteurs: { quiz: number; devoirs: number; brouillons: number; aCorriger: number };
}

export function useDevoirsDuFormateur() {
  return useQuery({
    queryKey: ['cours', 'devoirs-du-formateur'] as const,
    queryFn: () => appelApi<DevoirsDuFormateur>('/cours/devoirs'),
  });
}

export type GesteDevoir =
  | { geste: 'creer'; saisie: SaisieDevoir }
  | { geste: 'modifier'; devoirId: string; saisie: SaisieDevoir }
  | { geste: 'questions'; devoirId: string; questions: SaisieQuestion[] }
  | { geste: 'publication'; devoirId: string; publication: 'PUBLIER' | 'RETIRER' }
  | { geste: 'supprimer'; devoirId: string };

function appelDuGesteDevoir(coursId: string, geste: GesteDevoir): Promise<DevoirsDuCours> {
  const devoir = (devoirId: string, suite = '') => chemin(coursId, `/devoirs/${encodeURIComponent(devoirId)}${suite}`);
  switch (geste.geste) {
    case 'creer':
      return appelApi<DevoirsDuCours>(chemin(coursId, '/devoirs'), { methode: 'POST', corps: geste.saisie });
    case 'modifier':
      return appelApi<DevoirsDuCours>(devoir(geste.devoirId), { methode: 'PATCH', corps: geste.saisie });
    case 'questions':
      return appelApi<DevoirsDuCours>(devoir(geste.devoirId, '/questions'), { methode: 'PUT', corps: { questions: geste.questions } });
    case 'publication':
      return appelApi<DevoirsDuCours>(devoir(geste.devoirId, '/publication'), { methode: 'POST', corps: { geste: geste.publication } });
    case 'supprimer':
      return appelApi<DevoirsDuCours>(devoir(geste.devoirId), { methode: 'DELETE' });
  }
}

/** Chaque geste répond par tous les devoirs du cours ; le cours, modifié, se relit. */
export function useGesteDevoir(coursId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (geste: GesteDevoir) => appelDuGesteDevoir(coursId, geste),
    onSuccess: (vue) => {
      client.setQueryData(cleDevoirs(coursId), vue);
      void client.invalidateQueries({ queryKey: ['cours', 'resultats', coursId] });
      void client.invalidateQueries({ queryKey: cleCours(coursId), exact: true });
      void client.invalidateQueries({ queryKey: CLE_LISTE });
    },
    // Refusé : un apprenant a pu commencer entre-temps ; l'écran relit les devoirs.
    onError: () => void client.invalidateQueries({ queryKey: cleDevoirs(coursId) }),
  });
}

export function useResultatsDevoir(coursId: string, devoirId: string | null) {
  return useQuery({
    queryKey: ['cours', 'resultats', coursId, devoirId ?? ''],
    queryFn: () => appelApi<ResultatsDevoir>(chemin(coursId, `/devoirs/${encodeURIComponent(devoirId ?? '')}/resultats`)),
    enabled: Boolean(devoirId),
  });
}
